import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, ArrowLeft, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const CreateWedding = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { t, currencies } = useLanguage();

  const [step, setStep] = useState(1); // 1: wedding form, 2: verification upload
  const [weddingId, setWeddingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verificationDocs, setVerificationDocs] = useState([]); // list of uploaded doc_types
  const [uploadingDocType, setUploadingDocType] = useState(null); // doc_type currently being uploaded
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    country: 'DE',
    date: null,
    dresscode: '',
    price_per_guest: '',
    total_slots: '',
    currency: 'EUR',
    timezone: 'Europe/Berlin',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.date) {
      toast.error('Please select a date');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/weddings', {
        ...formData,
        date: format(formData.date, 'yyyy-MM-dd'),
        price_per_guest: parseFloat(formData.price_per_guest),
        total_slots: parseInt(formData.total_slots),
        images: [],
      });
      setWeddingId(response.data.id);
      toast.success('Wedding created successfully! Please upload verification documents.');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create wedding');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationUpload = async (docType, file) => {
    setUploadingDocType(docType);
    try {
      // Step 1: Upload image to get URL
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await api.post('/upload/image', formData);
      const docUrl = uploadResponse.data.url; // e.g., "/api/uploads/filename.jpg"

      // Step 2: Call verification endpoint
      await api.post(`/weddings/${weddingId}/verification`, {
        doc_type: docType,
        doc_url: docUrl,
      });

      // Add to uploaded docs
      setVerificationDocs(prev => [...prev, docType]);
      toast.success(`Document ${docType} uploaded successfully`);
    } catch (error) {
      toast.error(`Failed to upload ${docType}: ${error.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setUploadingDocType(null);
    }
  };

  const requiredDocs = ['id_front'];
  const marriageDocs = ['marriage_certificate', 'marriage_registration'];
  const hasRequired = requiredDocs.some(doc => verificationDocs.includes(doc));
  const hasMarriageProof = marriageDocs.some(doc => verificationDocs.includes(doc));
  const isVerificationComplete = hasRequired && hasMarriageProof;

  const countries = [
    { code: 'DE', name: 'Germany', tz: 'Europe/Berlin' },
    { code: 'US', name: 'United States', tz: 'America/New_York' },
    { code: 'FR', name: 'France', tz: 'Europe/Paris' },
    { code: 'ES', name: 'Spain', tz: 'Europe/Madrid' },
    { code: 'IT', name: 'Italy', tz: 'Europe/Rome' },
    { code: 'GB', name: 'United Kingdom', tz: 'Europe/London' },
    { code: 'AE', name: 'UAE', tz: 'Asia/Dubai' },
    { code: 'IN', name: 'India', tz: 'Asia/Kolkata' },
    { code: 'CN', name: 'China', tz: 'Asia/Shanghai' },
    { code: 'JP', name: 'Japan', tz: 'Asia/Tokyo' },
  ];

  return (
    <Layout showFooter={false}>
      <div className="min-h-screen bg-secondary py-8" data-testid="create-wedding-page">
        <div className="max-w-2xl mx-auto px-4">
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.back')}
          </Button>

          <Card>
            <CardHeader>
              <CardTitle className="font-serif text-2xl">
                {step === 1 ? t('wedding.create.title') : t('wedding.verification.uploadTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {step === 1 ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="title">{t('wedding.create.titleField')}</Label>
                    <Input
                      id="title"
                      name="title"
                      required
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Our Dream Wedding in the Alps"
                      data-testid="wedding-title"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">{t('wedding.create.description')}</Label>
                    <Textarea
                      id="description"
                      name="description"
                      required
                      rows={5}
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Tell guests about your wedding..."
                      data-testid="wedding-description"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="location">{t('wedding.create.location')}</Label>
                      <Input
                        id="location"
                        name="location"
                        required
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="Munich, Bavaria"
                        data-testid="wedding-location"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{t('wedding.create.country')}</Label>
                      <Select
                        value={formData.country}
                        onValueChange={(value) => {
                          const country = countries.find((c) => c.code === value);
                          setFormData({
                            ...formData,
                            country: value,
                            timezone: country?.tz || 'Europe/Berlin',
                          });
                        }}
                      >
                        <SelectTrigger data-testid="wedding-country">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('wedding.create.date')}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                            data-testid="wedding-date"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.date ? format(formData.date, 'PPP') : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={formData.date}
                            onSelect={(date) => setFormData({ ...formData, date })}
                            disabled={(date) => date < new Date()}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="dresscode">{t('wedding.create.dresscode')}</Label>
                      <Select
                        value={formData.dresscode}
                        onValueChange={(value) => setFormData({ ...formData, dresscode: value })}
                      >
                        <SelectTrigger data-testid="wedding-dresscode">
                          <SelectValue placeholder="Select dress code" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Formal">Formal / Black Tie</SelectItem>
                          <SelectItem value="Semi-Formal">Semi-Formal</SelectItem>
                          <SelectItem value="Casual Elegant">Casual Elegant</SelectItem>
                          <SelectItem value="Beach">Beach / Casual</SelectItem>
                          <SelectItem value="Traditional">Traditional</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="price_per_guest">{t('wedding.create.price')}</Label>
                      <Input
                        id="price_per_guest"
                        name="price_per_guest"
                        type="number"
                        step="0.01"
                        min="1"
                        required
                        value={formData.price_per_guest}
                        onChange={handleChange}
                        placeholder="150.00"
                        data-testid="wedding-price"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{t('wedding.create.currency')}</Label>
                      <Select
                        value={formData.currency}
                        onValueChange={(value) => setFormData({ ...formData, currency: value })}
                      >
                        <SelectTrigger data-testid="wedding-currency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {currencies.map((curr) => (
                            <SelectItem key={curr.code} value={curr.code}>
                              {curr.symbol} {curr.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="total_slots">{t('wedding.create.slots')}</Label>
                      <Input
                        id="total_slots"
                        name="total_slots"
                        type="number"
                        min="1"
                        max="100"
                        required
                        value={formData.total_slots}
                        onChange={handleChange}
                        placeholder="10"
                        data-testid="wedding-slots"
                      />
                    </div>
                  </div>

                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Platform Fee:</strong> WEDVAYA charges a 15% commission on each booking.
                      You'll receive {100 - 15}% of the price per guest after successful payment.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full rounded-full"
                    disabled={loading}
                    data-testid="create-wedding-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      t('wedding.create.submit')
                    )}
                  </Button>
                </form>
              ) : (
                <>
                  <p className="mb-4">{t('wedding.verification.uploadDesc')}</p>
                  <p className="mb-4 font-medium">{t('wedding.verification.requirement')}</p>

                  <div className="space-y-4">
                    {/* ID Front */}
                    <div>
                      <Label>{t('wedding.verification.docTypeIdFront')} {verificationDocs.includes('id_front') ? (
                        <span className="ml-2 text-xs text-green-600">({t('wedding.verification.uploaded')})</span>
                      ) : null}</Label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleVerificationUpload('id_front', file);
                            e.target.value = ''; // reset input
                          }
                        }}
                        disabled={loading || uploadingDocType === 'id_front'}
                        className="mb-2 block w-full"
                      />
                      {uploadingDocType === 'id_front' && (
                        <p className="text-xs text-muted-foreground">{t('wedding.verification.uploading')}...</p>
                      )}
                    </div>

                    {/* ID Back */}
                    <div>
                      <Label>{t('wedding.verification.docTypeIdBack')} {verificationDocs.includes('id_back') ? (
                        <span className="ml-2 text-xs text-green-600">({t('wedding.verification.uploaded')})</span>
                      ) : null}</Label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleVerificationUpload('id_back', file);
                            e.target.value = '';
                          }
                        }}
                        disabled={loading || uploadingDocType === 'id_back'}
                        className="mb-2 block w-full"
                      />
                      {uploadingDocType === 'id_back' && (
                        <p className="text-xs text-muted-foreground">{t('wedding.verification.uploading')}...</p>
                      )}
                    </div>

                    {/* Marriage Certificate */}
                    <div>
                      <Label>{t('wedding.verification.docTypeMarriageCert')} {verificationDocs.includes('marriage_certificate') ? (
                        <span className="ml-2 text-xs text-green-600">({t('wedding.verification.uploaded')})</span>
                      ) : null}</Label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleVerificationUpload('marriage_certificate', file);
                            e.target.value = '';
                          }
                        }}
                        disabled={loading || uploadingDocType === 'marriage_certificate'}
                        className="mb-2 block w-full"
                      />
                      {uploadingDocType === 'marriage_certificate' && (
                        <p className="text-xs text-muted-foreground">{t('wedding.verification.uploading')}...</p>
                      )}
                    </div>

                    {/* Marriage Registration */}
                    <div>
                      <Label>{t('wedding.verification.docTypeMarriageReg')} {verificationDocs.includes('marriage_registration') ? (
                        <span className="ml-2 text-xs text-green-600">({t('wedding.verification.uploaded')})</span>
                      ) : null}</Label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleVerificationUpload('marriage_registration', file);
                            e.target.value = '';
                          }
                        }}
                        disabled={loading || uploadingDocType === 'marriage_registration'}
                        className="mb-2 block w-full"
                      />
                      {uploadingDocType === 'marriage_registration' && (
                        <p className="text-xs text-muted-foreground">{t('wedding.verification.uploading')}...</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6">
                    <Button
                      onClick={() => navigate('/host/dashboard')}
                      className="w-full rounded-full"
                      disabled={!isVerificationComplete || loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Finalizing...
                        </>
                      ) : (
                        isVerificationComplete ? t('wedding.verification.submit') : t('wedding.verification.incomplete')
                      )}
                    </Button>
                    {!isVerificationComplete && !loading && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        {t('wedding.verification.completeHint')}
                      </p>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default CreateWedding;